import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import Select from 'react-select';
import { getClientDomain, getToken, translate } from "../utils/Common";
import AuthContext from "../contexts/auth";
import ThemeContext from "../contexts/theme";
import { getReactSelectStyles } from "../utils/reactSelectThemeStyles";
import debounce from 'lodash.debounce';
import { BsFillPeopleFill } from "react-icons/bs";
import { SelectShell } from "@/components/ui/select-shell";

const ClientsSearchList = (props) => {
  const userContext = useContext(AuthContext);
  const themeContext = useContext(ThemeContext);
  const theme = themeContext?.theme || "light";
  const selectStyles = useMemo(() => getReactSelectStyles(theme, { hasIcon: true }), [theme]);
  const [clients, setClients] = useState([]);
  // initialOption ({ value, label }) permite precargar un cliente ya asignado
  const [selectedOption, setSelectedOption] = useState(props.initialOption ?? null);
  const [loading, setLoading] = useState(false);
  const [searchCache, setSearchCache] = useState({});

  const searchClients = async (term) => {
    setLoading(true);
    try {
      const response = await fetch(`${getClientDomain(2)}${!props.allClients ? '/dashboard/clients/search' : '/dashboard/clients/searchAll'}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Bearer ${getToken()}`,
        },
        body: JSON.stringify({
          buscarCliente: term.toUpperCase(),
          idUsuario: props.idUsuarioSelected === 'all' ? null : (props?.idUsuarioSelected ?? userContext.user?.Ruc),
          idTipoCliente: props.idTipoCliente,
        }),
      });

      const result = await response.json();
      if (result.code === 200 && result.payload && Array.isArray(result.payload)) {
        const limitedResults = result.payload.slice(0, 100);

        const formattedResults = limitedResults.map(item => ({
          value: item.IdCliente,
          label: `${item.IdCliente} - ${item.Nombre ?? ""} ${item.Apellidos ?? ""}`,
          fullName: `${item.Nombre ?? ""} ${item.Apellidos ?? ""}`.trim(),
          ...item
        }));

        setClients(formattedResults);
        setSearchCache(prevCache => ({ ...prevCache, [term]: formattedResults }));

        if (props.autoSelectClient && result.payload.length > 0 && !selectedOption) {
          const content = {
            value: result.payload[0].IdCliente,
            label: `${result.payload[0].IdCliente} - ${result.payload[0].Nombre ?? ""} ${result.payload[0].Apellidos ?? ""}`,
            ...result.payload[0]
          };
          setSelectedOption(content);
          props.setSelectOption(content.value);
        }
      } else {
        setClients([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(debounce((term) => {
    if (searchCache[term]) {
      setClients(searchCache[term]);
    } else {
      searchClients(term);
    }
  }, 300), [searchCache]);

  const handleInputChange = (inputValue, actionMeta) => {
    // react-select limpia el input al seleccionar una opcion y al cerrar el menu.
    // Sin este guard, cada seleccion/blur disparaba una busqueda completa que
    // recargaba las opciones y hacia parpadear el isLoading.
    if (actionMeta && actionMeta.action !== "input-change") return;

    if (inputValue) {
      debouncedSearch(inputValue);
    } else {
      // Realiza una búsqueda vacía para obtener todos los clientes
      searchClients('');
    }
  };

  // Cargar clientes iniciales al montar el componente
  useEffect(() => {
    // Realizar búsqueda inicial con término vacío
    searchClients('');

    // Limpiar debounce al desmontar
    return () => {
      debouncedSearch.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resetear cuando cambia idUsuarioSelected
  useEffect(() => {
    setSelectedOption(props.initialOption ?? null);
    searchClients('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.idUsuarioSelected]);

  useEffect(() => {
    if (props.resetFlag) {
      setSelectedOption(null);
    }
  }, [props.resetFlag]);

  const selectOptions = useMemo(() => clients, [clients]);

  return (
    <SelectShell icon={<BsFillPeopleFill size={16} />} error={props?.errors?.buscarCliente}>
      <Select
        value={selectedOption}
        onInputChange={handleInputChange}
        options={selectOptions}
        isLoading={loading}
        className="fs-5 w-100"
        classNamePrefix="react-select"
        placeholder={translate("global.search.alerted")}
        name="buscarCliente"
        onChange={(e) => {
          setSelectedOption(e);
          props.setSelectOption(e ? e.value : null);

          // Guardar el cliente seleccionado en el cache de la sesión
          if (e && e.value) {
            const currentCache = JSON.parse(localStorage.getItem('clients-cache') || '[]');
            const existingClient = currentCache.find(c => c.value === e.value);

            if (!existingClient) {
              const newCache = [...currentCache, e];
              localStorage.setItem('clients-cache', JSON.stringify(newCache));
            }
          }
        }}
        required={props.isRequired}
        isClearable
        styles={selectStyles}
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
      />
    </SelectShell>
  );
};

export default ClientsSearchList;